import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';

import {useRecentlyViewedProducts} from '../../hook/useRecentlyViewedProducts';
import {productUrl, recentlyViewedUrl} from '../../util/url';

import './RecentlyViewedPanel.css';

const MAX_VISIBLE = 5;
const CHIP_THUMB_COUNT = 3;

/**
 * 상품 상세 PC 화면 오른쪽 여백에 떠 있는 최근 본 상품 패널.
 * 지금 보고 있는 상품은 빼고 이전에 본 상품을 최근 순으로 최대 5개 겹쳐 쌓아 보여주고, 나머지는 전체보기 페이지로 보낸다.
 * 저장된 가격·판매 상태는 본 시점 값이라 실제와 다를 수 있어 보여주지 않는다.
 * 기록은 localStorage에만 있어 SSR과 hydrate 전에는 그리지 않는다. 화면에 고정된 패널이라 늦게 나타나도 본문이 밀리지 않는다.
 *
 * 화면 폭별 표시는 CSS가 정한다.
 * - 1440px 이상: 본문 오른쪽 여백에 카드를 항상 펼쳐 둔다.
 * - 1280~1439px: 여백이 카드보다 좁아 작은 칩만 두고, 칩을 눌렀을 때만 카드를 본문 위로 띄운다.
 * - 1280px 미만: 숨긴다.
 */
export default function RecentlyViewedPanel({ currentProductId }) {
    const {items, hydrated} = useRecentlyViewedProducts();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const products = useMemo(
        () => items.filter((product) => product.id !== String(currentProductId)).slice(0, MAX_VISIBLE),
        [items, currentProductId]
    );

    // 다른 상품으로 이동하면 닫는다
    useEffect(() => {
        setOpen(false);
    }, [currentProductId]);

    // 열려 있는 동안에만 바깥 클릭과 ESC로 닫는다
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    if (!hydrated || products.length === 0) {
        return null;
    }

    return (
        <div ref={rootRef} className={`RecentlyViewedPanel${open ? ' is-open' : ''}`}>
            <button
                type="button"
                className="RecentlyViewedPanel-chip"
                aria-label="최근 본 상품 열기"
                aria-expanded={open}
                aria-controls="recently-viewed-panel-card"
                onClick={() => setOpen((prev) => !prev)}
            >
                {products.slice(0, CHIP_THUMB_COUNT).map((product) => (
                    <img
                        key={product.id}
                        className="RecentlyViewedPanel-chip-thumb"
                        width="36"
                        height="36"
                        alt=""
                        src={product.resizedSmallImages[0]}
                    />
                ))}
                <span className="RecentlyViewedPanel-chip-caret" aria-hidden="true" />
            </button>

            <aside id="recently-viewed-panel-card" className="RecentlyViewedPanel-card" aria-label="최근 본 상품">
                <p className="RecentlyViewedPanel-title">
                    최근 본<br />상품
                </p>

                <ul className="RecentlyViewedPanel-list">
                    {products.map((product) => (
                        <li key={product.id} className="RecentlyViewedPanel-item">
                            <Link to={productUrl(product)} className="RecentlyViewedPanel-thumb" aria-label={product.title}>
                                <img width="60" height="60" alt="" src={product.resizedSmallImages[0]} />
                            </Link>
                            {/* 썸네일만으로는 어떤 상품인지 알기 어려워 마우스를 올리면 왼쪽에 상품명을 보여준다 */}
                            <span className="RecentlyViewedPanel-tooltip" aria-hidden="true">
                                {product.title}
                            </span>
                        </li>
                    ))}
                </ul>

                <Link to={recentlyViewedUrl()} className="RecentlyViewedPanel-more">
                    전체보기
                </Link>
            </aside>
        </div>
    );
}
RecentlyViewedPanel.propTypes = {
    currentProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
